import React, { useState } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import { User, Mail, Phone, Lock, Eye, EyeOff, Loader2, Save, KeyRound } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../../contexts/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const StudentProfile = () => {
  const { user, refreshUser } = useAuth();

  const [profileForm, setProfileForm] = useState({
    name: user?.name || '',
    phone: user?.phone || ''
  });
  const [savingProfile, setSavingProfile] = useState(false);

  const [passwordForm, setPasswordForm] = useState({
    current_password: '',
    new_password: '',
    confirm_password: ''
  });
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [savingPassword, setSavingPassword] = useState(false);

  const handleProfileSave = async (e) => {
    e.preventDefault();
    if (!profileForm.name.trim()) {
      toast.error('Name is required');
      return;
    }
    setSavingProfile(true);
    try {
      await axios.put(`${API_URL}/api/student/profile`, {
        name: profileForm.name.trim(),
        phone: profileForm.phone.trim() || null
      });
      await refreshUser();
      toast.success('Profile updated');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update profile');
    } finally {
      setSavingProfile(false);
    }
  };

  const handlePasswordSave = async (e) => {
    e.preventDefault();
    if (!passwordForm.current_password || !passwordForm.new_password || !passwordForm.confirm_password) {
      toast.error('Please fill in all password fields');
      return;
    }
    if (passwordForm.new_password.length < 8) {
      toast.error('New password must be at least 8 characters');
      return;
    }
    if (passwordForm.new_password !== passwordForm.confirm_password) {
      toast.error('Passwords do not match');
      return;
    }
    setSavingPassword(true);
    try {
      await axios.post(`${API_URL}/api/auth/change-password`, {
        current_password: passwordForm.current_password,
        new_password: passwordForm.new_password
      });
      toast.success('Password changed successfully');
      setPasswordForm({ current_password: '', new_password: '', confirm_password: '' });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to change password');
    } finally {
      setSavingPassword(false);
    }
  };

  return (
    <div className="space-y-6 max-w-2xl" data-testid="student-profile-page">
      <div>
        <h1 className="text-2xl lg:text-3xl font-bold text-[#1A2E6E] font-['Sora']">My Profile</h1>
        <p className="text-[#1A2E6E]/60 mt-1">Manage your personal information and password</p>
      </div>

      {/* Profile Info */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white rounded-xl border border-[#E8EAF0] overflow-hidden"
      >
        <div className="px-6 py-4 border-b border-[#E8EAF0] flex items-center gap-3 bg-gradient-to-r from-[#1A2E6E]/5 to-transparent">
          <User className="w-5 h-5 text-[#1A2E6E]" />
          <div>
            <h3 className="font-semibold text-[#1A2E6E]">Personal Information</h3>
            <p className="text-sm text-[#1A2E6E]/60">Update your name and phone number</p>
          </div>
        </div>
        <form onSubmit={handleProfileSave} className="p-6 space-y-4">
          {/* Avatar preview */}
          <div className="flex items-center gap-4 mb-2">
            <div className="w-16 h-16 rounded-full bg-[#4CAF50] flex items-center justify-center text-white font-bold text-2xl shrink-0">
              {user?.name?.charAt(0)?.toUpperCase() || 'S'}
            </div>
            <div>
              <p className="font-semibold text-[#1A2E6E]">{user?.name}</p>
              <p className="text-sm text-[#1A2E6E]/60 flex items-center gap-1">
                <Mail size={13} /> {user?.email}
              </p>
            </div>
          </div>

          <div>
            <label htmlFor="student-profile-name" className="block text-sm font-medium text-[#1A2E6E] mb-1">Full Name</label>
            <div className="relative">
              <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#1A2E6E]/40" />
              <input
                id="student-profile-name"
                type="text"
                value={profileForm.name}
                onChange={e => setProfileForm(f => ({ ...f, name: e.target.value }))}
                className="w-full pl-10 pr-4 py-3 rounded-xl border border-[#D1D5DB] focus:border-[#1A2E6E] focus:ring-2 focus:ring-[#1A2E6E]/10 outline-none"
              />
            </div>
          </div>

          <div>
            <label htmlFor="student-profile-phone" className="block text-sm font-medium text-[#1A2E6E] mb-1">
              Phone <span className="text-[#1A2E6E]/40">(optional)</span>
            </label>
            <div className="relative">
              <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#1A2E6E]/40" />
              <input
                id="student-profile-phone"
                type="tel"
                value={profileForm.phone}
                onChange={e => setProfileForm(f => ({ ...f, phone: e.target.value }))}
                placeholder="+55 11 99999-9999"
                className="w-full pl-10 pr-4 py-3 rounded-xl border border-[#D1D5DB] focus:border-[#1A2E6E] focus:ring-2 focus:ring-[#1A2E6E]/10 outline-none"
              />
            </div>
          </div>

          <div className="bg-[#1A2E6E]/5 rounded-xl p-3 flex items-center gap-2 text-sm text-[#1A2E6E]/70">
            <Mail size={14} className="shrink-0" />
            To change your email address, contact your teacher.
          </div>

          <button
            type="submit"
            disabled={savingProfile}
            className="flex items-center gap-2 px-6 py-3 bg-[#1A2E6E] text-white rounded-xl hover:bg-[#16275C] transition-colors font-medium disabled:opacity-50"
          >
            {savingProfile ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
            Save Changes
          </button>
        </form>
      </motion.div>

      {/* Change Password */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.05 }}
        className="bg-white rounded-xl border border-[#E8EAF0] overflow-hidden"
      >
        <div className="px-6 py-4 border-b border-[#E8EAF0] flex items-center gap-3 bg-gradient-to-r from-[#F5A623]/5 to-transparent">
          <KeyRound className="w-5 h-5 text-[#F5A623]" />
          <div>
            <h3 className="font-semibold text-[#1A2E6E]">Change Password</h3>
            <p className="text-sm text-[#1A2E6E]/60">Update your login password</p>
          </div>
        </div>
        <form onSubmit={handlePasswordSave} className="p-6 space-y-4">
          <div>
            <label htmlFor="student-profile-current-password" className="block text-sm font-medium text-[#1A2E6E] mb-1">Current Password</label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#1A2E6E]/40" />
              <input
                id="student-profile-current-password"
                type={showCurrent ? 'text' : 'password'}
                value={passwordForm.current_password}
                onChange={e => setPasswordForm(f => ({ ...f, current_password: e.target.value }))}
                className="w-full pl-10 pr-12 py-3 rounded-xl border border-[#D1D5DB] focus:border-[#1A2E6E] focus:ring-2 focus:ring-[#1A2E6E]/10 outline-none"
              />
              <button type="button" onClick={() => setShowCurrent(v => !v)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-[#1A2E6E]/40 hover:text-[#1A2E6E]">
                {showCurrent ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
          </div>

          <div>
            <label htmlFor="student-profile-new-password" className="block text-sm font-medium text-[#1A2E6E] mb-1">New Password</label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#1A2E6E]/40" />
              <input
                id="student-profile-new-password"
                type={showNew ? 'text' : 'password'}
                value={passwordForm.new_password}
                onChange={e => setPasswordForm(f => ({ ...f, new_password: e.target.value }))}
                placeholder="At least 8 characters"
                className="w-full pl-10 pr-12 py-3 rounded-xl border border-[#D1D5DB] focus:border-[#1A2E6E] focus:ring-2 focus:ring-[#1A2E6E]/10 outline-none"
              />
              <button type="button" onClick={() => setShowNew(v => !v)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-[#1A2E6E]/40 hover:text-[#1A2E6E]">
                {showNew ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
          </div>

          <div>
            <label htmlFor="student-profile-confirm-password" className="block text-sm font-medium text-[#1A2E6E] mb-1">Confirm New Password</label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#1A2E6E]/40" />
              <input
                id="student-profile-confirm-password"
                type={showConfirm ? 'text' : 'password'}
                value={passwordForm.confirm_password}
                onChange={e => setPasswordForm(f => ({ ...f, confirm_password: e.target.value }))}
                placeholder="Repeat new password"
                className="w-full pl-10 pr-12 py-3 rounded-xl border border-[#D1D5DB] focus:border-[#1A2E6E] focus:ring-2 focus:ring-[#1A2E6E]/10 outline-none"
              />
              <button type="button" onClick={() => setShowConfirm(v => !v)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-[#1A2E6E]/40 hover:text-[#1A2E6E]">
                {showConfirm ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
          </div>

          <button
            type="submit"
            disabled={savingPassword}
            className="flex items-center gap-2 px-6 py-3 bg-[#F5A623] text-white rounded-xl hover:bg-[#E09000] transition-colors font-medium disabled:opacity-50"
          >
            {savingPassword ? <Loader2 size={16} className="animate-spin" /> : <KeyRound size={16} />}
            Change Password
          </button>
        </form>
      </motion.div>
    </div>
  );
};

export default StudentProfile;
